import { useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Input, Modal, Typography } from 'antd';
import type { InputRef } from 'antd';
import { EnterOutlined, SearchOutlined } from '@ant-design/icons';

import { scoreMenuMatch, searchableMenu, type MenuContext } from '../lib/menu';

const { Text } = Typography;

interface Props {
  open: boolean;
  ctx: MenuContext;
  onClose: () => void;
  /** 고른 화면의 키. App.tsx 가 /app/<key> 로 보낸다. */
  onPick: (key: string) => void;
}

/**
 * 메뉴 검색 (Ctrl+K / Cmd+K).
 *
 * ## 왜 필요한가
 * Hub 는 메뉴가 두 단계다. 통합관리자에게는 「내 기업」 묶음까지 붙어 화면이
 * 20개 가까이 된다. 「요금제가 과금 아래였나 시스템 아래였나」를 떠올리는 것보다
 * 이름을 치는 쪽이 빠르다. WORK&CRM 에 이미 같은 것이 있어 조작도 같게 뒀다.
 *
 * ## 목록이 메뉴와 어긋나지 않는 이유
 * 뒤지는 목록은 `searchableMenu(ctx)` 가 **좌측 메뉴와 같은 함수(buildMenu)의
 * 결과**를 펼쳐서 만든다. 선언을 따로 읽지 않으므로, 지원 모드라 숨긴 화면이
 * 검색에만 튀어나오는 일이 없다.
 *
 * ## 열릴 때만 그린다
 * App.tsx 가 open 일 때만 마운트한다. 검색어와 커서를 비우는 코드가 따로
 * 필요 없고, 닫아 둔 동안 키 입력을 듣지도 않는다.
 */
export default function MenuSearch({ open, ctx, onClose, onPick }: Props) {
  const [keyword, setKeyword] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<InputRef>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // ctx 는 매 렌더 새 객체라 값으로 의존성을 건다.
  const items = useMemo(
    () => searchableMenu(ctx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.isPlatformAdmin, ctx.support],
  );

  const results = useMemo(() => {
    if (!keyword.trim()) return items;
    return items
      .map((item) => ({ item, score: scoreMenuMatch(item, keyword) }))
      .filter((entry) => entry.score >= 0)
      .sort((a, b) => a.score - b.score || a.item.label.localeCompare(b.item.label))
      .map((entry) => entry.item);
  }, [items, keyword]);

  // 검색어가 바뀌면 커서를 처음으로 (렌더 중 상태 조정 — 한 프레임 어긋나지 않는다)
  const [lastKeyword, setLastKeyword] = useState(keyword);
  if (lastKeyword !== keyword) {
    setLastKeyword(keyword);
    setCursor(0);
  }

  // 열릴 때 입력창으로 바로 들어간다. antd Modal 의 등장 애니메이션 뒤라야 먹는다.
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(timer);
  }, [open]);

  // 고른 줄이 보이는 곳에 있도록
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [cursor, results]);

  const go = (key?: string) => {
    if (!key) return;
    onPick(key);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((prev) => (results.length ? (prev + 1) % results.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((prev) => (results.length ? (prev - 1 + results.length) % results.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(results[cursor]?.key);
    }
    // Esc 는 antd Modal 이 받아 onCancel 로 넘긴다.
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      destroyOnClose
      width={520}
      style={{ top: '12vh' }}
      styles={{ body: { padding: 0 } }}
    >
      <Input
        ref={inputRef}
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="메뉴 검색 (예: 구성원, 요금제, 로그)"
        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        variant="borderless"
        size="large"
        style={{ borderBottom: '1px solid #f0f0f0', borderRadius: 0, padding: '12px 16px' }}
      />

      <div ref={listRef} style={{ maxHeight: '48vh', overflowY: 'auto', padding: '8px 0' }}>
        {results.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="일치하는 메뉴가 없습니다."
            style={{ margin: '32px 0' }}
          />
        ) : (
          results.map((item, index) => (
            <div
              key={item.key}
              data-active={index === cursor}
              onMouseEnter={() => setCursor(index)}
              onClick={() => go(item.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 16px',
                cursor: 'pointer',
                background: index === cursor ? '#f0f5ff' : undefined,
              }}
            >
              <span style={{ color: '#8c8c8c', display: 'flex' }}>{item.icon}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{item.label}</div>
                {item.trail && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {item.trail}
                  </Text>
                )}
              </span>
              {index === cursor && <EnterOutlined style={{ color: '#1677ff' }} />}
            </div>
          ))
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 16,
          padding: '8px 16px',
          borderTop: '1px solid #f0f0f0',
          background: '#fafafa',
        }}
      >
        <Text type="secondary" style={{ fontSize: 11 }}>↑ ↓ 이동</Text>
        <Text type="secondary" style={{ fontSize: 11 }}>Enter 열기</Text>
        <Text type="secondary" style={{ fontSize: 11 }}>Esc 닫기</Text>
      </div>
    </Modal>
  );
}
